import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';
import { LeadForm } from '../components/LeadForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Home as HomeIcon,
  Calculator as CalculatorIcon,
  MapPin,
  Building2,
  Ruler,
  Calendar,
  Sparkles,
  ArrowRight,
  Info,
  TrendingUp,
  MessageCircle,
  Menu,
  X,
  Scale
} from 'lucide-react';
import { agentInfo } from '../data/mock';
import { trackCalculatorUse, trackContactClick } from '../utils/analytics';

// Precios promedio por m² en USD por barrio - FALLBACK estático
// Se usan solo cuando la API de RE/MAX no tiene suficientes muestras
// del mes en curso para un barrio específico.
// Fuente: RE/MAX Argentina, Reporte Inmobiliario, Zonaprop, Mudafy, La Nación
const PRICE_DATA = {
  departamento: {
    // CABA
    'Palermo': 3398,
    'Belgrano': 2850,
    'Recoleta': 2459,
    'Núñez': 2800,
    'Puerto Madero': 4500,
    'Caballito': 2200,
    'Villa Crespo': 2400,
    'Almagro': 2100,
    'Villa Urquiza': 2300,
    'Colegiales': 2600,
    'Barrio Norte': 2500,
    'Flores': 1800,
    'Villa del Parque': 1900,
    'Devoto': 2000,
    'Saavedra': 2100,
    'Coghlan': 2200,
    'Boedo': 1950,
    'San Telmo': 2100,
    'Monserrat': 1800,
    'Balvanera': 1700,
    'La Boca': 1200,
    // GBA Norte
    'Vicente López': 3550,
    'Olivos': 3136,
    'La Lucila': 3650,
    'Martínez': 2400,
    'San Isidro': 3000,
    'Acassuso': 2800,
    'Beccar': 2500,
    'San Fernando': 2200,
    'Tigre Centro': 2400,
    'Nordelta': 3200,
    'Don Torcuato': 2000,
    'Pacheco': 1900,
    // GBA Oeste
    'Ramos Mejía': 1800,
    'Haedo': 1700,
    'Morón': 1750,
    'Castelar': 1850,
    'Ituzaingó': 1700,
    // GBA Sur
    'Avellaneda': 1600,
    'Quilmes': 1500,
    'Lanús': 1450,
    'Lomas de Zamora': 1550,
    'Adrogué': 1700,
    'Banfield': 1600,
    'Otro CABA': 2100,
    'Otro GBA': 1800
  },
  casa: {
    // CABA
    'Palermo': 3300,
    'Belgrano': 2800,
    'Recoleta': 2400,
    'Núñez': 3100,
    'Puerto Madero': 4000,
    'Caballito': 1900,
    'Villa Crespo': 2100,
    'Almagro': 1800,
    'Villa Urquiza': 2000,
    'Colegiales': 2300,
    'Barrio Norte': 2200,
    'Flores': 1500,
    'Villa del Parque': 1600,
    'Devoto': 1700,
    'Saavedra': 1800,
    'Coghlan': 1900,
    'Boedo': 1650,
    'San Telmo': 1800,
    'Monserrat': 1500,
    'Balvanera': 1400,
    'La Boca': 1000,
    // GBA Norte
    'Vicente López': 3400,
    'Olivos': 3000,
    'La Lucila': 3500,
    'Martínez': 2600,
    'San Isidro': 3200,
    'Acassuso': 2900,
    'Beccar': 2400,
    'San Fernando': 2100,
    'Tigre Centro': 2300,
    'Nordelta': 3000,
    'Don Torcuato': 1900,
    'Pacheco': 1800,
    // GBA Oeste
    'Ramos Mejía': 1700,
    'Haedo': 1600,
    'Morón': 1650,
    'Castelar': 1750,
    'Ituzaingó': 1600,
    // GBA Sur
    'Avellaneda': 1500,
    'Quilmes': 1400,
    'Lanús': 1350,
    'Lomas de Zamora': 1450,
    'Adrogué': 1600,
    'Banfield': 1500,
    'Otro CABA': 1826,
    'Otro GBA': 1600
  }
};

// Categorías de barrios para el selector
const NEIGHBORHOODS_BY_ZONE = {
  'CABA': [
    'Palermo', 'Belgrano', 'Recoleta', 'Núñez', 'Puerto Madero', 
    'Caballito', 'Villa Crespo', 'Almagro', 'Villa Urquiza', 'Colegiales',
    'Barrio Norte', 'Flores', 'Villa del Parque', 'Devoto', 'Saavedra',
    'Coghlan', 'Boedo', 'San Telmo', 'Monserrat', 'Balvanera', 'La Boca', 'Otro CABA'
  ],
  'GBA Norte': [
    'Vicente López', 'Olivos', 'La Lucila', 'Martínez', 'San Isidro',
    'Acassuso', 'Beccar', 'San Fernando', 'Tigre Centro', 'Nordelta',
    'Don Torcuato', 'Pacheco'
  ],
  'GBA Oeste': [
    'Ramos Mejía', 'Haedo', 'Morón', 'Castelar', 'Ituzaingó'
  ],
  'GBA Sur': [
    'Avellaneda', 'Quilmes', 'Lanús', 'Lomas de Zamora', 'Adrogué', 'Banfield', 'Otro GBA'
  ]
};

// Factores de ajuste por antigüedad (porcentaje sobre el precio base)
const ANTIGUEDAD_FACTOR = {
  'a-estrenar': 1.15,
  '1-5': 1.05,
  '6-15': 1.0,
  '16-30': 0.90,
  '31-50': 0.80,
  'mas-50': 0.70
};

// Factores de ajuste por estado
const ESTADO_FACTOR = {
  'excelente': 1.10,
  'muy-bueno': 1.05,
  'bueno': 1.0,
  'regular': 0.90,
  'a-refaccionar': 0.75
};

// Factor por ambientes (departamentos)
const AMBIENTES_FACTOR = {
  '1': 0.95,  // Monoambiente
  '2': 1.0,   // 2 ambientes (base)
  '3': 1.02,
  '4': 1.03,
  '5': 1.04
};

const Calculator = () => {
  useSeo({
    title: 'Calculadora del Valor de tu Propiedad en CABA y Zona Norte | Vendetucasa.ar',
    description: 'Estimá el precio de tu casa o departamento al instante con datos reales por barrio. 67 barrios cubiertos, actualizados mensualmente.',
    canonical: 'https://vendetucasa.ar/calculadora',
    ogUrl: 'https://vendetucasa.ar/calculadora',
  });

  // Lead form (replaces WhatsApp CTAs)
  const [calcLeadOpen, setCalcLeadOpen] = useState(false);
  const [formData, setFormData] = useState({
    propertyType: '',
    neighborhood: '',
    size: 50,
    rooms: '2',
    age: '',
    condition: ''
  });
  
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Precios reales fetched desde el backend (datos vivos de RE/MAX)
  const [livePrices, setLivePrices] = useState({ departamento: {}, casa: {} });
  const [priceMonthYear, setPriceMonthYear] = useState(null);
  const [priceSources, setPriceSources] = useState([
    'Vendetucasa.ar',
    'Reporte Inmobiliario',
    'Zonaprop',
    'Mudafy',
    'Mercado Libre Inmuebles'
  ]);

  // Mes en curso dinámico para el footer informativo
  const currentMonthLabel = (() => {
    const formatter = new Intl.DateTimeFormat('es-AR', {
      month: 'long',
      year: 'numeric'
    });
    const raw = formatter.format(new Date());
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();

  // Convierte "2025-12" en "Diciembre 2025" si está disponible
  const dataMonthLabel = (() => {
    if (!priceMonthYear) return currentMonthLabel;
    const [y, m] = priceMonthYear.split('-').map(Number);
    if (!y || !m) return currentMonthLabel;
    const formatter = new Intl.DateTimeFormat('es-AR', {
      month: 'long',
      year: 'numeric'
    });
    const raw = formatter.format(new Date(y, m - 1, 1));
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/calculator/prices`)
      .then((res) => {
        if (cancelled || !res?.data) return;
        const payload = res.data;
        if (payload?.data) {
          setLivePrices({
            departamento: payload.data.departamento || {},
            casa: payload.data.casa || {}
          });
        }
        if (payload?.month_year) setPriceMonthYear(payload.month_year);
        if (Array.isArray(payload?.sources) && payload.sources.length > 0) {
          setPriceSources(payload.sources);
        }
      })
      .catch((err) => {
        // Falla silenciosa: seguimos con el fallback estático
        console.warn('No se pudieron obtener precios en vivo:', err?.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const calculatePrice = () => {
    if (!formData.propertyType || !formData.neighborhood || !formData.age || !formData.condition) {
      return;
    }

    // Prioridad: precio real (RE/MAX en vivo) > precio estático > default 2100
    const liveEntry = livePrices?.[formData.propertyType]?.[formData.neighborhood];
    const liveBase = liveEntry?.price_per_m2;
    const staticBase = PRICE_DATA[formData.propertyType][formData.neighborhood];
    const basePrice = liveBase || staticBase || 2100;
    const usedLive = !!liveBase;

    const ageFactor = ANTIGUEDAD_FACTOR[formData.age] || 1.0;
    const conditionFactor = ESTADO_FACTOR[formData.condition] || 1.0;
    const roomsFactor = formData.propertyType === 'departamento' 
      ? (AMBIENTES_FACTOR[formData.rooms] || 1.0) 
      : 1.0;

    const adjustedPricePerM2 = basePrice * ageFactor * conditionFactor * roomsFactor;
    const totalPrice = adjustedPricePerM2 * formData.size;

    // Rango de estimación (±10%)
    const minPrice = totalPrice * 0.90;
    const maxPrice = totalPrice * 1.10;

    setResult({
      pricePerM2: Math.round(adjustedPricePerM2),
      totalPrice: Math.round(totalPrice),
      minPrice: Math.round(minPrice),
      maxPrice: Math.round(maxPrice),
      neighborhood: formData.neighborhood,
      propertyType: formData.propertyType === 'departamento' ? 'Departamento' : 'Casa',
      size: formData.size,
      sampleCount: liveEntry?.sample_count || 0,
      usedLiveData: usedLive
    });
    setShowResult(true);
    
    // Track calculator usage
    trackCalculatorUse({
      property_type: formData.propertyType,
      zone: formData.neighborhood,
      area: formData.size,
      estimated_price: Math.round(totalPrice)
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const scrollToForm = () => {
    window.location.href = '/#appraisal-form';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="relative">
              <svg viewBox="0 0 100 100" className="w-10 h-10">
                <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                <circle cx="25" cy="25" r="5" fill="white"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-[#E31C23]">vende</span>
              <span className="text-slate-600">tu</span>
              <span className="text-[#E31C23]">casa</span>
              <span className="text-[#003DA5]">.ar</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-5">
            <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</Link>
            <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</Link>
            <Link to="/calculadora" className="text-[#E31C23] font-medium">Calculadora</Link>
            <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Barrios</Link>
            <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Propiedades</Link>
            <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Noticias</Link>
            <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">FAQ</Link>
            <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Contacto</Link>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link 
              to="/calculadora" 
              className="calculator-pulse hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#003DA5] text-white hover:bg-[#E31C23] hover:scale-110 transition-all duration-200 shadow-md"
              title="Calculadora de Precios"
            >
              <CalculatorIcon className="w-5 h-5" />
            </Link>
            <Button asChild className="hidden sm:flex bg-[#E31C23] hover:bg-[#C11A1F] text-white">
              <Link to="/#appraisal-form">Conocé el Valor de tu Casa</Link>
            </Button>
            
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Sobre Mí
              </Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Servicios
              </Link>
              <Link to="/calculadora" className="text-[#E31C23] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Calculadora
              </Link>
              <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Barrios
              </Link>
              <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Propiedades
              </Link>
              <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Noticias
              </Link>
              <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                FAQ
              </Link>
              <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Contacto
              </Link>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2">
                <Link to="/#appraisal-form" onClick={() => setMobileMenuOpen(false)}>Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#003DA5] to-[#0051D5] py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <CalculatorIcon className="w-5 h-5 text-white mr-2" />
              <span className="text-white text-sm font-medium">Herramienta Gratuita</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Calculadora de Precio
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Obtené una estimación del valor de tu propiedad basada en datos actualizados del mercado inmobiliario de Buenos Aires
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Form */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#003DA5] flex items-center">
                    <Sparkles className="w-6 h-6 mr-2 text-[#E31C23]" />
                    Datos de tu Propiedad
                  </CardTitle>
                  <CardDescription>
                    Completá los datos para obtener una estimación
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tipo de propiedad */}
                  <div>
                    <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                      <Building2 className="w-4 h-4 mr-2" />
                      Tipo de Propiedad
                    </Label>
                    <Select 
                      value={formData.propertyType} 
                      onValueChange={(value) => setFormData({...formData, propertyType: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="departamento">Departamento</SelectItem>
                        <SelectItem value="casa">Casa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Barrio */}
                  <div>
                    <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      Barrio / Localidad
                    </Label>
                    <Select 
                      value={formData.neighborhood} 
                      onValueChange={(value) => setFormData({...formData, neighborhood: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar zona" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <div className="px-2 py-1.5 text-xs font-semibold text-[#003DA5] bg-blue-50">CABA</div>
                        {NEIGHBORHOODS_BY_ZONE['CABA'].map((barrio) => (
                          <SelectItem key={barrio} value={barrio}>{barrio}</SelectItem>
                        ))}
                        <div className="px-2 py-1.5 text-xs font-semibold text-[#003DA5] bg-blue-50 mt-1">GBA Norte</div>
                        {NEIGHBORHOODS_BY_ZONE['GBA Norte'].map((barrio) => (
                          <SelectItem key={barrio} value={barrio}>{barrio}</SelectItem>
                        ))}
                        <div className="px-2 py-1.5 text-xs font-semibold text-[#003DA5] bg-blue-50 mt-1">GBA Oeste</div>
                        {NEIGHBORHOODS_BY_ZONE['GBA Oeste'].map((barrio) => (
                          <SelectItem key={barrio} value={barrio}>{barrio}</SelectItem>
                        ))}
                        <div className="px-2 py-1.5 text-xs font-semibold text-[#003DA5] bg-blue-50 mt-1">GBA Sur</div>
                        {NEIGHBORHOODS_BY_ZONE['GBA Sur'].map((barrio) => (
                          <SelectItem key={barrio} value={barrio}>{barrio}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Superficie */}
                  <div>
                    <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                      <Ruler className="w-4 h-4 mr-2" />
                      Superficie: {formData.size} m²
                    </Label>
                    <Slider
                      value={[formData.size]}
                      onValueChange={(value) => setFormData({...formData, size: value[0]})}
                      min={20}
                      max={500}
                      step={5}
                      className="mt-2"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>20 m²</span>
                      <span>500 m²</span>
                    </div>
                  </div>

                  {/* Ambientes (solo para departamentos) */}
                  {formData.propertyType === 'departamento' && (
                    <div>
                      <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                        <HomeIcon className="w-4 h-4 mr-2" />
                        Ambientes
                      </Label>
                      <Select 
                        value={formData.rooms} 
                        onValueChange={(value) => setFormData({...formData, rooms: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Cantidad de ambientes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Monoambiente</SelectItem>
                          <SelectItem value="2">2 Ambientes</SelectItem>
                          <SelectItem value="3">3 Ambientes</SelectItem>
                          <SelectItem value="4">4 Ambientes</SelectItem>
                          <SelectItem value="5">5+ Ambientes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Antigüedad */}
                  <div>
                    <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      Antigüedad
                    </Label>
                    <Select 
                      value={formData.age} 
                      onValueChange={(value) => setFormData({...formData, age: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar antigüedad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a-estrenar">A estrenar</SelectItem>
                        <SelectItem value="1-5">1 a 5 años</SelectItem>
                        <SelectItem value="6-15">6 a 15 años</SelectItem>
                        <SelectItem value="16-30">16 a 30 años</SelectItem>
                        <SelectItem value="31-50">31 a 50 años</SelectItem>
                        <SelectItem value="mas-50">Más de 50 años</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Estado */}
                  <div>
                    <Label className="text-[#003DA5] font-medium flex items-center mb-2">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Estado General
                    </Label>
                    <Select 
                      value={formData.condition} 
                      onValueChange={(value) => setFormData({...formData, condition: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excelente">Excelente (reciclado/premium)</SelectItem>
                        <SelectItem value="muy-bueno">Muy Bueno</SelectItem>
                        <SelectItem value="bueno">Bueno</SelectItem>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="a-refaccionar">A refaccionar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={calculatePrice}
                    className="w-full bg-[#E31C23] hover:bg-[#C11A1F] text-white text-lg py-6"
                    disabled={!formData.propertyType || !formData.neighborhood || !formData.age || !formData.condition}
                  >
                    <CalculatorIcon className="w-5 h-5 mr-2" />
                    Calcular Estimación
                  </Button>
                </CardContent>
              </Card>

              {/* Result */}
              <div className="space-y-6">
                {showResult && result ? (
                  <>
                    <Card className="shadow-lg border-2 border-[#003DA5]">
                      <CardHeader className="bg-gradient-to-r from-[#003DA5] to-[#0051D5] text-white rounded-t-lg">
                        <CardTitle className="text-2xl flex items-center">
                          <TrendingUp className="w-6 h-6 mr-2" />
                          Estimación de Valor
                        </CardTitle>
                        <CardDescription className="text-blue-100">
                          {result.propertyType} en {result.neighborhood} - {result.size} m²
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="text-center mb-4">
                          <p className="text-sm text-slate-600 mb-2">Valor Estimado</p>
                          <p className="text-4xl md:text-5xl font-bold text-[#003DA5]">
                            {formatCurrency(result.totalPrice)}
                          </p>
                        </div>

                        {/* Live data badge */}
                        <div className="flex justify-center mb-6">
                          {result.usedLiveData ? (
                            <div
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium"
                              data-testid="calculator-live-badge"
                              title={`Mediana calculada a partir de ${result.sampleCount} propiedades activas en RE/MAX Argentina`}
                            >
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                              </span>
                              Datos en vivo · {result.sampleCount} muestra{result.sampleCount === 1 ? '' : 's'} de RE/MAX
                            </div>
                          ) : (
                            <div
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-medium"
                              data-testid="calculator-fallback-badge"
                              title="No hay suficientes propiedades activas para este barrio en RE/MAX; se usa un promedio histórico."
                            >
                              <span className="inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
                              Estimación basada en promedio histórico
                            </div>
                          )}
                        </div>
                        
                        <div className="bg-slate-50 rounded-lg p-4 mb-6">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-slate-600">Rango estimado:</span>
                          </div>
                          <div className="flex justify-between items-center text-lg font-semibold">
                            <span className="text-slate-700">{formatCurrency(result.minPrice)}</span>
                            <span className="text-slate-400">—</span>
                            <span className="text-slate-700">{formatCurrency(result.maxPrice)}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-600 mb-1">Precio por m²</p>
                            <p className="text-lg font-bold text-[#003DA5]">
                              {formatCurrency(result.pricePerM2)}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-600 mb-1">Superficie</p>
                            <p className="text-lg font-bold text-[#003DA5]">
                              {result.size} m²
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-800 mb-4">
                          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <p>
                            Esta es una estimación basada en promedios del mercado. 
                            Para un valor preciso, solicitá un análisis profesional.
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-lg bg-gradient-to-br from-[#E31C23] to-[#C11A1F] text-white">
                      <CardContent className="pt-6">
                        <h3 className="text-xl font-bold mb-3">
                          ¿Querés un valor más preciso?
                        </h3>
                        <p className="text-red-100 mb-4">
                          Solicitá un análisis comparativo de mercado y te contacto en menos de 24 horas.
                        </p>
                        <div className="flex flex-col gap-3">
                          <Button 
                            onClick={scrollToForm}
                            className="w-full bg-white text-[#E31C23] hover:bg-slate-100"
                          >
                            Solicitar Análisis de Mercado
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                          <Button
                            onClick={() => setCalcLeadOpen(true)}
                            variant="outline"
                            className="w-full border-white text-white hover:bg-white/10"
                            data-testid="calc-consult-lead"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Recibir análisis personalizado
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card className="shadow-lg h-full flex items-center justify-center min-h-[400px]">
                    <CardContent className="text-center py-12">
                      <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CalculatorIcon className="w-10 h-10 text-[#003DA5]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#003DA5] mb-3">
                        Completá los datos
                      </h3>
                      <p className="text-slate-600 max-w-sm mx-auto">
                        Ingresá la información de tu propiedad para obtener una estimación del valor de mercado
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Info Section */}
            <Card className="mt-8 bg-slate-50 border-0">
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-[#003DA5] mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2" />
                  ¿Cómo calculamos el valor?
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm text-slate-600">
                  <div>
                    <h4 className="font-medium text-slate-800 mb-2">📊 Datos del Mercado</h4>
                    <p>Utilizamos precios promedio por m² actualizados de los principales portales inmobiliarios de Argentina.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-800 mb-2">📍 Ubicación</h4>
                    <p>El barrio es uno de los factores más importantes. Zonas como Puerto Madero y Palermo tienen valores premium.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-800 mb-2">🏠 Características</h4>
                    <p>Ajustamos el valor según antigüedad, estado de conservación y tipo de propiedad.</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-4" data-testid="calculator-sources-line">
                  Fuentes: {priceSources.join(', ')}. Datos actualizados a {dataMonthLabel}.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <svg viewBox="0 0 100 100" className="w-16 h-16">
                    <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                    <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                    <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                    <circle cx="25" cy="25" r="5" fill="white"/>
                  </svg>
                </div>
                <span className="text-4xl font-bold">
                  <span className="text-white">vende</span>
                  <span className="text-blue-200">tu</span>
                  <span className="text-white">casa</span>
                  <span className="text-blue-300">.ar</span>
                </span>
              </div>
            </div>
            <p className="text-blue-100 mb-2">{agentInfo.name} - Negocios Inmobiliarios</p>
            <p className="text-blue-200 text-sm">
              © {new Date().getFullYear()} Vendetucasa.ar - Todos los derechos reservados
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Contactar Button — opens neutral lead form */}
      <button
        type="button"
        onClick={() => setCalcLeadOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-[#E31C23] hover:bg-red-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="Contactanos"
        data-testid="calc-floating-contact"
      >
        <MessageCircle className="w-7 h-7" />
      </button>

      {/* Lead capture modal */}
      <LeadForm
        open={calcLeadOpen}
        onClose={() => setCalcLeadOpen(false)}
        defaultLeadType="valuacion"
        title="Análisis del valor de tu propiedad"
        sourcePage="/calculadora"
      />
    </div>
  );
};

export default Calculator;
